/**
 * What to react to a note *with*.
 *
 * Until reactions were sendable there was nothing to model: every kind-7 this
 * client published carried `+`, so "react" and "like" were the same word. They are
 * not the same thing, and conflating them breaks in two directions the moment a
 * second emoji is reachable:
 *
 *  - **The heart is the like, not "any reaction".** If reacting with 🔥 lights up
 *    the heart, the row claims the reader liked a note they only marked, and the
 *    control's own label flips to "Remove reaction" — so the next press withdraws
 *    something the reader never sent.
 *  - **Withdrawing is per emoji.** A single "delete my reactions to this note" is
 *    the obvious implementation and it is wrong: un-hearting a note you also
 *    reacted to with 🔥 would silently delete the 🔥 as well.
 *
 * Both are avoided by giving every reaction a *key* and doing all the comparing on
 * keys. The keys here are deliberately the same strings `noteReactions.ts` groups
 * chips by, so "have I already reacted with this" and "is my avatar on this chip"
 * can never disagree — they are the same question asked of the same identity.
 */

import { isEmojiShortcode } from "@setu/protocol";
import { LIKE_KEY, type ReactionGroup } from "./noteReactions";

/**
 * One thing a reader can react with.
 *
 * A custom emoji is a separate case rather than a `content` string with an
 * optional URL beside it, because the two fields are meaningless apart: a
 * `:shortcode:` with no `emoji` tag renders as literal text in every client, and
 * an `emoji` tag whose shortcode is absent from the content is dead weight. Making
 * them one variant means a caller cannot construct half of a custom reaction.
 */
export type ReactionChoice =
  | {
      readonly kind: "emoji";
      /** The literal reaction content — an emoji grapheme, or `+` for a like. */
      readonly content: string;
    }
  | {
      readonly kind: "custom";
      readonly shortcode: string;
      /** Image URL, published in a NIP-30 `emoji` tag beside the content. */
      readonly url: string;
    };

/** The plain like: NIP-25's `+`, and what the heart control sends. */
export const LIKE: ReactionChoice = { kind: "emoji", content: LIKE_KEY };

/**
 * A custom emoji reaction, or undefined when the shortcode could never resolve.
 *
 * The validating constructor is the only way to build the `custom` variant, so
 * `buildReaction` can emit the `emoji` tag without re-checking. A shortcode
 * containing a colon or a space cannot be matched by any scanner that treats `:`
 * as the delimiter — publishing one would produce a reaction that renders as
 * literal text everywhere, including here.
 */
export function customReaction(
  shortcode: string,
  url: string,
): ReactionChoice | undefined {
  if (!isEmojiShortcode(shortcode) || url === "") return undefined;
  return { kind: "custom", shortcode, url };
}

/** The event content this choice publishes. */
export function reactionContent(choice: ReactionChoice): string {
  return choice.kind === "custom" ? `:${choice.shortcode}:` : choice.content;
}

/**
 * The grouping identity this choice would land under.
 *
 * Must agree with `reactionEventKey` for the event this choice produces. The empty
 * string collapses to `+` for the same reason it does there: NIP-25 treats an empty
 * reaction as a like, so a reader who reacted with one has liked the note.
 */
export function reactionChoiceKey(choice: ReactionChoice): string {
  if (choice.kind === "custom") return `:${choice.shortcode}:`;
  return choice.content === "" ? LIKE_KEY : choice.content;
}

/**
 * Re-react with what someone else already used.
 *
 * This is the whole of custom-emoji *sending* in Setu today, and it is a
 * deliberate scope: a chip carries the shortcode and the URL the original reactor
 * published, so pressing it needs no emoji-pack browser and no kind-30030 fetch.
 * Undefined when the group cannot be turned back into a choice, which the caller
 * renders as a non-clickable chip rather than a button that fails.
 */
export function choiceForGroup(
  group: ReactionGroup,
): ReactionChoice | undefined {
  if (group.imageUrl === undefined) {
    return group.key === "" ? undefined : { kind: "emoji", content: group.key };
  }
  // `key` is `:shortcode:` for every grouped custom emoji — see `bucketFor` — but
  // this reads the code back out rather than trusting that, because the fallback
  // is a static chip and the alternative is publishing a malformed tag.
  const shortcode =
    group.key.startsWith(":") && group.key.endsWith(":")
      ? group.key.slice(1, -1)
      : "";
  return customReaction(shortcode, group.imageUrl);
}
